import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { ContentSkeleton } from 'src/components/ContentSkeleton';

import {
  ChartBody,
  ChartCanvas,
  ChartCard,
  ChartCardDuration,
  ChartCardHeader,
  ChartCardTitle,
  ChartEmpty,
  ChartError,
  ChartLegend,
  ChartMainValue,
  ChartSubValue,
  LegendDot,
} from './styled';

export type ChartPoint = {
  label: string;
  value: number;
};

type DashboardChartCardProps = {
  title: string;
  durationLabel: string;
  mainValue: string;
  subtitle?: string;
  points: ChartPoint[];
  variant?: 'line' | 'bar';
  loading?: boolean;
  error?: string | null;
  legendLabel?: string;
};

const PADDING = { top: 8, right: 12, bottom: 28, left: 40 };

const niceMax = (max: number) => {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const normalized = max / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

const formatAxis = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
  return String(Math.round(value));
};

const buildSmoothPath = (points: Array<{ x: number; y: number }>) => {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const cx = (current.x + next.x) / 2;
    d += ` C ${cx} ${current.y}, ${cx} ${next.y}, ${next.x} ${next.y}`;
  }
  return d;
};

const DashboardChartCard: React.FC<DashboardChartCardProps> = ({
  title,
  durationLabel,
  mainValue,
  subtitle,
  points,
  variant = 'line',
  loading,
  error,
  legendLabel = 'All',
}) => {
  const theme = useTheme();
  const width = 360;
  const height = 140;
  const plotW = width - PADDING.left - PADDING.right;
  const plotH = height - PADDING.top - PADDING.bottom;

  const chart = useMemo(() => {
    const values = points.map((p) => p.value);
    const max = niceMax(Math.max(...values, 0));
    const ticks = [max, max * 0.66, max * 0.33, 0];
    const n = Math.max(points.length, 1);

    // A line runs edge to edge, but bars are centred in equal bands: drawing a
    // bar on the left edge would put half its width over the y-axis labels.
    const band = plotW / n;
    const coords = points.map((p, i) => {
      let x: number;
      if (variant === 'bar') {
        x = PADDING.left + (i + 0.5) * band;
      } else if (n === 1) {
        x = PADDING.left + plotW / 2;
      } else {
        x = PADDING.left + (i / (n - 1)) * plotW;
      }
      const y = PADDING.top + plotH - (p.value / max) * plotH;
      return { ...p, x, y };
    });

    const labelIndexes =
      points.length <= 6
        ? points.map((_, i) => i)
        : Array.from({ length: 6 }, (_, i) => Math.round((i * (points.length - 1)) / 5));

    return { max, ticks, coords, band, labelIndexes: Array.from(new Set(labelIndexes)) };
  }, [points, plotH, plotW, variant]);

  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{title}</ChartCardTitle>
        <ChartCardDuration>{durationLabel}</ChartCardDuration>
      </ChartCardHeader>
      <ChartMainValue>{mainValue}</ChartMainValue>
      <ChartSubValue>{subtitle || '\u00A0'}</ChartSubValue>
      <ChartBody>
        {loading ? (
          <ContentSkeleton variant="chart" />
        ) : error ? (
          <ChartError>{error}</ChartError>
        ) : !points.length ? (
          <ChartEmpty>No data for this period</ChartEmpty>
        ) : (
          <>
            <ChartCanvas>
              <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" role="img" aria-label={`${title} chart`}>
                {chart.ticks.map((tick) => {
                  const y = PADDING.top + plotH - (tick / chart.max) * plotH;
                  return (
                    <g key={`tick-${tick}`}>
                      <line
                        x1={PADDING.left}
                        x2={width - PADDING.right}
                        y1={y}
                        y2={y}
                        stroke={theme.grey}
                        strokeDasharray="2 3"
                      />
                      <text
                        x={PADDING.left - 10}
                        y={y + 3}
                        textAnchor="end"
                        fill={theme.dark}
                        fontSize="10"
                      >
                        {formatAxis(tick)}
                      </text>
                    </g>
                  );
                })}

                {variant === 'line' ? (
                  <>
                    <path
                      d={buildSmoothPath(chart.coords)}
                      fill="none"
                      stroke={theme.primary}
                      strokeWidth="2"
                    />
                    {chart.coords.map((p) => (
                      <circle key={`${p.label}-${p.x}`} cx={p.x} cy={p.y} r="3.5" fill={theme.primary} />
                    ))}
                  </>
                ) : (
                  chart.coords.map((p, i) => {
                    const barW = Math.max(4, Math.min(chart.band * 0.6, 18));
                    const barH = Math.max(0, PADDING.top + plotH - p.y);
                    return (
                      <rect
                        key={`${p.label}-${i}`}
                        x={p.x - barW / 2}
                        y={p.y}
                        width={barW}
                        height={barH}
                        rx="1"
                        fill={theme.primary}
                      />
                    );
                  })
                )}

                {chart.labelIndexes.map((idx) => {
                  const p = chart.coords[idx];
                  if (!p) return null;
                  const isFirst = idx === 0;
                  const isLast = idx === chart.coords.length - 1;
                  // Bars sit inside their band, so every label centres under its
                  // bar. Line points sit on the plot edges and would overflow.
                  const anchor =
                    variant === 'bar'
                      ? 'middle'
                      : isFirst && isLast
                        ? 'middle'
                        : isFirst
                          ? 'start'
                          : isLast
                            ? 'end'
                            : 'middle';
                  return (
                    <text
                      key={`label-${idx}`}
                      x={p.x}
                      y={height - 6}
                      textAnchor={anchor}
                      fill={theme.dark}
                      fontSize="9"
                    >
                      {p.label}
                    </text>
                  );
                })}
              </svg>
            </ChartCanvas>
            <ChartLegend>
              <LegendDot />
              {legendLabel}
            </ChartLegend>
          </>
        )}
      </ChartBody>
    </ChartCard>
  );
};

export default DashboardChartCard;
